import React, { useMemo, useState } from 'react'
import { Translate } from 'react-localize-redux'
import {
  Box,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  CardActionArea,
  Badge,
} from '@material-ui/core'
import PropTypes from 'prop-types'
import FavoriteIcon from '@material-ui/icons/Favorite'
import { SubLink } from 'containers'
import EditIcon from '@material-ui/icons/Edit'
import AddCircleRoundedIcon from '@material-ui/icons/AddCircleRounded'
import useTeamFeature from 'hooks/useTeamFeature'
import useFeatureAccessGuard from 'hooks/useFeatureAccessGuard'
import LockIconComponent from 'components/UpgradeMembership/LockIconComponent'
import LockIcon from '@material-ui/icons/Lock'
import PremiumContentDialog from '../PremiumContentDialog'
import fallbackImageUrl from '../../assets/images/login-coach.jpg'
import {
  MuiCard,
  CardMedia,
  CardActions,
  MuiBox,
} from './components/StyledComponents'
import TrainingPackageViewDialog from './components/TrainingPackageViewDialog'
import { useStyles } from './styles'
import AddTrainingPackageToTrainingDialog from './components/AddTrainingPackageToTrainingDialog'
import { TRAINING_PACKAGE_EDIT_ROUTE } from 'constants/routes'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const TrainingPackageCard = ({
  trainingPackage,
  onFavoriteToggle,
  showAddToTrainingButton,
  showAddToPhaseButton,
  onTrainingPackageSelect,
  showTrainingPackageActions,
}) => {
  const classes = useStyles()
  const [trainingPackageViewDialogOpen, setTrainingPackageViewDialogOpen] =
    useState(false)
  const [addToTrainingModalOpen, setAddToTrainingModalOpen] = useState(false)
  const [premiumDialogOpen, setPremiumDialogOpen] = useState(false)

  const featureGuard = useFeatureAccessGuard()

  const canUpdate = trainingPackage.can.includes('update')

  const { enabled: hasAdvancedTrainingFeaturesEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.advanced_training_features_enabled
  )
  const { enabled: hasTrainingPackagesEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.training_packages_enabled
  )

  const toggleTrainingPackageViewDialog = () => {
    if (featureGuard(hasTrainingPackagesEnabled)) return

    setTrainingPackageViewDialogOpen(!trainingPackageViewDialogOpen)
  }

  const handleCardClick = () => {
    if (
      !hasTrainingPackagesEnabled ||
      (trainingPackage.locked &&
        trainingPackage.locked_ff === 'cb_premium_content')
    ) {
      featureGuard()
    } else if (
      trainingPackage.locked &&
      trainingPackage.locked_ff !== 'cb_premium_content'
    ) {
      setPremiumDialogOpen(true)
    } else {
      setTrainingPackageViewDialogOpen(true)
    }
  }

  const handleClosePremiumDialog = () => {
    setPremiumDialogOpen(false)
  }

  const imageUrl =
    trainingPackage.relationships.images.length > 0
      ? trainingPackage.relationships.images[
          trainingPackage.relationships.images.length - 1
        ].full_url
      : fallbackImageUrl

  const calculateTotalDuration = () => {
    const drills = trainingPackage.relationships
    const sumDurations = (tempDrills) => {
      return tempDrills.reduce(
        (total, drill) => total + (drill.duration || 0),
        0
      )
    }
    const totalDuration =
      sumDurations(drills.main_part_drills || []) +
      sumDurations(drills.warmup_drills || []) +
      sumDurations(drills.final_part_drills || [])

    return totalDuration
  }
  const toggleAddToTrainingDialog = () => {
    if (featureGuard(hasAdvancedTrainingFeaturesEnabled)) return

    setAddToTrainingModalOpen(!addToTrainingModalOpen)
  }

  const totalDuration = useMemo(calculateTotalDuration, [
    trainingPackage.relationships,
  ])

  const handleAddToPhase = (e) => {
    if (featureGuard(hasAdvancedTrainingFeaturesEnabled)) return

    e.stopPropagation()
    onTrainingPackageSelect(trainingPackage.uuid)
  }

  return (
    <>
      {trainingPackageViewDialogOpen && (
        <TrainingPackageViewDialog
          trainingPackage={trainingPackage}
          handleDialogOpen={toggleTrainingPackageViewDialog}
          open={trainingPackageViewDialogOpen}
          duration={totalDuration}
        />
      )}
      {addToTrainingModalOpen && (
        <AddTrainingPackageToTrainingDialog
          open={addToTrainingModalOpen}
          handleClose={toggleAddToTrainingDialog}
          trainingPackage={trainingPackage}
        />
      )}
      {premiumDialogOpen && (
        <PremiumContentDialog
          open={premiumDialogOpen}
          onClose={handleClosePremiumDialog}
          lockedFeatureFlag={trainingPackage.locked_ff}
        />
      )}
      <MuiCard>
        <CardActionArea onClick={handleCardClick}>
          <CardMedia image={imageUrl} />
          <CardContent>
            <Typography gutterBottom variant="h4" component="h4" noWrap>
              {trainingPackage.title}
              {!hasTrainingPackagesEnabled || trainingPackage.locked ? (
                <LockIconComponent
                  context="secondaryButton"
                  style={{ float: 'right' }}
                />
              ) : null}
            </Typography>
            <MuiBox display="flex">
              <Box>
                <Typography>
                  <Translate id="training.duration" />
                </Typography>
                {totalDuration} min
              </Box>
              <Box>
                <Typography>
                  <Translate id="drills.players" /> :
                </Typography>
                {trainingPackage.number_of_players}
              </Box>
            </MuiBox>
          </CardContent>
        </CardActionArea>
        <Divider />
        <CardActions>
          {!trainingPackage.locked && (
            <>
              <Box>
                {showAddToTrainingButton && (
                  <Tooltip title={<Translate id="training.add-to-training" />}>
                    <IconButton
                      onClick={toggleAddToTrainingDialog}
                      size="small"
                      color="primary"
                    >
                      <Badge
                        className={classes.lockBadgeOnIconButton}
                        badgeContent={
                          !hasAdvancedTrainingFeaturesEnabled ? (
                            <LockIcon color="primary" fontSize="small" />
                          ) : null
                        }
                        overlap="circular"
                      >
                        <AddCircleRoundedIcon />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                )}
                {showAddToPhaseButton && (
                  <Tooltip title={<Translate id="training.add-to-training" />}>
                    <IconButton
                      onClick={handleAddToPhase}
                      size="small"
                      color="primary"
                    >
                      <Badge
                        className={classes.lockBadgeOnIconButton}
                        badgeContent={
                          !hasAdvancedTrainingFeaturesEnabled ? (
                            <LockIcon color="primary" fontSize="small" />
                          ) : null
                        }
                        overlap="circular"
                      >
                        <AddCircleRoundedIcon />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {showTrainingPackageActions && (
                <>
                  {canUpdate && (
                    <Tooltip title={<Translate id="button.edit" />}>
                      <IconButton
                        size="small"
                        className={classes.editDrillIcon}
                        {...(!hasTrainingPackagesEnabled
                          ? {
                              onClick: () => featureGuard(),
                            }
                          : {
                              component: SubLink,
                              to: TRAINING_PACKAGE_EDIT_ROUTE.populateURLParams(
                                {
                                  trainingPackageUuid: trainingPackage.uuid,
                                }
                              ),
                            })}
                      >
                        <Badge
                          className={classes.lockBadgeOnIconButton}
                          badgeContent={
                            !hasTrainingPackagesEnabled ? (
                              <LockIcon color="primary" fontSize="small" />
                            ) : null
                          }
                          overlap="circular"
                        >
                          <EditIcon fontSize="small" />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
            </>
          )}
          <Tooltip title={<Translate id="drills.favorite" />}>
            <IconButton onClick={onFavoriteToggle} size="small">
              <FavoriteIcon
                color={trainingPackage.favorite ? 'primary' : 'inherit'}
                fontSize="small"
              />
            </IconButton>
          </Tooltip>
        </CardActions>
      </MuiCard>
    </>
  )
}

TrainingPackageCard.defaultProps = {
  showAddToTrainingButton: false,
  showAddToPhaseButton: false,
  onTrainingPackageSelect: () => {},
  showTrainingPackageActions: false,
}

TrainingPackageCard.propTypes = {
  trainingPackage: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    number_of_players: PropTypes.number.isRequired,
    favorite: PropTypes.bool.isRequired,
    can: PropTypes.arrayOf(PropTypes.string),
    relationships: PropTypes.shape({
      images: PropTypes.arrayOf(
        PropTypes.shape({
          full_url: PropTypes.string.isRequired,
        })
      ).isRequired,
      main_part_drills: PropTypes.arrayOf(
        PropTypes.shape({
          duration: PropTypes.number.isRequired,
        })
      ).isRequired,
      warmup_drills: PropTypes.arrayOf(
        PropTypes.shape({
          duration: PropTypes.number.isRequired,
        })
      ).isRequired,
      final_part_drills: PropTypes.arrayOf(
        PropTypes.shape({
          duration: PropTypes.number.isRequired,
        })
      ).isRequired,
    }).isRequired,
    locked: PropTypes.bool,
    locked_ff: PropTypes.string,
  }).isRequired,
  onFavoriteToggle: PropTypes.func.isRequired,
  showAddToTrainingButton: PropTypes.bool,
  showAddToPhaseButton: PropTypes.bool,
  onTrainingPackageSelect: PropTypes.func,
  showTrainingPackageActions: PropTypes.bool,
}

export default TrainingPackageCard
